import {
  LocationShort,
  LocationShortType,
  UserBadgeShort,
  UserBadgeType,
  UserShort,
  UserShortType,
} from '@weave/schema-gen-ts/dist/schemas/threads-of-success/v2/api.pb';
import { cloneDeep } from 'lodash-es';
import averageResponseTimeAnimation from './animations/averageResponseTime.json';
import busiestDayAnimation from './animations/busiestDay.json';
import callHeroAnimation from './animations/callHero.json';
import callsPlacedReceivedAnimation from './animations/callsPlacedReceived.json';
import cashCowAnimation from './animations/cashCow.json';
import chatterboxAnimation from './animations/chatterbox.json';
import fastestResponseTimeAnimation from './animations/fastestResponseTime.json';
import formsReceivedAnimation from './animations/formsReceived.json';
import greenThumbAnimation from './animations/greenThumb.json';
import hoursInWeaveAnimation from './animations/hoursInWeave.json';
import lightningMcqueenAnimation from './animations/lightningMcqueen.json';
import longestCallDurationAnimation from './animations/longestCallDuration.json';
// import missCongenialityAnimation from './animations/missCongeniality.json'; // TODO: add back when ready
import paymentsCollectedAnimation from './animations/paymentsCollected.json';
import phoneCallsMadeAnimation from './animations/phoneCallsMade.json';
import textsSentReceivedAnimation from './animations/textsSentReceived.json';
import topCommunicationChannelMessagesAnimation from './animations/topCommunicationChannelMessages.json';
import topCommunicationChannelPaymentsAnimation from './animations/topCommunicationChannelPayments.json';
import topCommunicationChannelPhonesAnimation from './animations/topCommunicationChannelPhones.json';
import topConnectorAnimation from './animations/topConnector.json';
import totalMessagesSentAnimation from './animations/totalMessagesSent.json';
import totalPatientsContactedAnimation from './animations/totalPatientsContacted.json';
import weavePowerUserAnimation from './animations/weavePowerUser.json';

const updateAverageResponseTimeAnimation = (value: number) => {
  const newAnimation = cloneDeep(averageResponseTimeAnimation) as any;
  newAnimation.layers[5].ef[0].ef[0].v.k[1].s[0] = value;
  return newAnimation;
};

const daysOfWeekMap: Record<number, string> = {
  1: 'Sunday',
  2: 'Monday',
  3: 'Tuesday',
  4: 'Wednesday',
  5: 'Thursday',
  6: 'Friday',
  7: 'Saturday',
};

const updateBusiestDayAnimation = (value: number) => {
  const newAnimation = cloneDeep(busiestDayAnimation) as any;
  newAnimation.layers[6].t.d.k[0].s.t = daysOfWeekMap[value];
  return newAnimation;
};

const updateCallsPlacedReceivedAnimation = (inbound: number, outbound: number) => {
  const newAnimation = cloneDeep(callsPlacedReceivedAnimation) as any;
  newAnimation.layers[4].ef[1].ef[0].v.k = inbound; // calls received
  newAnimation.layers[9].ef[1].ef[0].v.k = outbound; // calls placed
  return newAnimation;
};

const updateFastestResponseTimeAnimation = (value: number) => {
  const newAnimation = cloneDeep(fastestResponseTimeAnimation) as any;
  newAnimation.layers[8].ef[0].ef[0].v.k[1].s[0] = value;
  return newAnimation;
};

const updateFormsReceivedAnimation = (value: number) => {
  const newAnimation = cloneDeep(formsReceivedAnimation) as any;
  newAnimation.layers[5].ef[1].ef[0].v.k = value;
  return newAnimation;
};

export const updateHoursInWeaveAnimation = (value: number) => {
  const newAnimation = cloneDeep(hoursInWeaveAnimation) as any;
  newAnimation.layers[6].ef[0].ef[0].v.k[1].s[0] = value;
  return newAnimation;
};

const updateLongestCallDurationAnimation = (value: number) => {
  const newAnimation = cloneDeep(longestCallDurationAnimation) as any;
  newAnimation.layers[2].ef[1].ef[0].v.k = value;
  return newAnimation;
};

const updatePaymentsCollectedAnimation = (value: number) => {
  const newAnimation = cloneDeep(paymentsCollectedAnimation) as any;
  newAnimation.layers[3].ef[1].ef[0].v.k = value;
  return newAnimation;
};

const updatePhoneCallsMadeAnimation = (value: number) => {
  const newAnimation = cloneDeep(phoneCallsMadeAnimation) as any;
  newAnimation.layers[2].ef[1].ef[0].v.k = value;
  return newAnimation;
};

const updateTextsSentReceivedAnimation = (inbound: number, outbound: number) => {
  const newAnimation = cloneDeep(textsSentReceivedAnimation) as any;
  newAnimation.layers[5].ef[1].ef[0].v.k = inbound; // texts received
  newAnimation.layers[10].ef[1].ef[0].v.k = outbound; // texts sent
  return newAnimation;
};

const updateTotalMessagesSentAnimation = (value: number) => {
  const newAnimation = cloneDeep(totalMessagesSentAnimation) as any;
  newAnimation.layers[2].ef[0].ef[0].v.k[1].s[0] = value;
  return newAnimation;
};

const updateTotalPatientsContactedAnimation = (value: number) => {
  const newAnimation = cloneDeep(totalPatientsContactedAnimation) as any;
  newAnimation.layers[4].ef[1].ef[0].v.k = value;
  return newAnimation;
};

export const getUpdatedLocationShortType = (locationShort: LocationShort) => {
  const { type, value1, value2 } = locationShort;
  const numberedValue1 = Number(value1);
  const numberedValue2 = Number(value2);

  switch (type) {
    case LocationShortType.LOCATION_SHORT_TYPE_AVERAGE_RESPONSE_TIME:
      return updateAverageResponseTimeAnimation(numberedValue1);
    case LocationShortType.LOCATION_SHORT_TYPE_BUSIEST_DAY_OF_THE_WEEK:
      return updateBusiestDayAnimation(numberedValue1);
    case LocationShortType.LOCATION_SHORT_TYPE_FORMS_RECEIVED:
      return updateFormsReceivedAnimation(numberedValue1);
    case LocationShortType.LOCATION_SHORT_TYPE_PATIENTS_CONTACTED:
      return updateTotalPatientsContactedAnimation(numberedValue1);
    case LocationShortType.LOCATION_SHORT_TYPE_PAYMENTS_COLLECTED:
      return updatePaymentsCollectedAnimation(numberedValue1);
    case LocationShortType.LOCATION_SHORT_TYPE_PHONE_CALLS:
      return updateCallsPlacedReceivedAnimation(numberedValue1, numberedValue2);
    case LocationShortType.LOCATION_SHORT_TYPE_SMS_MESSAGES:
      return updateTextsSentReceivedAnimation(numberedValue1, numberedValue2);
  }
};

export const getUpdatedUserBadgeType = (userBadgeShort: UserBadgeShort) => {
  const { type } = userBadgeShort;
  switch (type) {
    case UserBadgeType.USER_BADGE_TYPE_CALL_HERO:
      return callHeroAnimation;
    case UserBadgeType.USER_BADGE_TYPE_CASH_COW:
      return cashCowAnimation;
    case UserBadgeType.USER_BADGE_TYPE_CHATTERBOX:
      return chatterboxAnimation;
    case UserBadgeType.USER_BADGE_TYPE_GREEN_THUMB:
      return greenThumbAnimation;
    case UserBadgeType.USER_BADGE_TYPE_LIGHTNING_MCQUEEN:
      return lightningMcqueenAnimation;
    // case UserBadgeType.USER_BADGE_TYPE_MISS_CONGENIALITY:
    //   return missCongenialityAnimation; // TODO: add back when ready
    case UserBadgeType.USER_BADGE_TYPE_WEAVE_POWER_USER:
      return weavePowerUserAnimation;
    case UserBadgeType.USER_BADGE_TYPE_TOP_CONNECTOR:
      return topConnectorAnimation;
    default:
      return null;
  }
};

export const getUpdatedUserShortType = (userShort: UserShort) => {
  const { type, value1 } = userShort;
  const numberedValue1 = Number(value1);
  switch (type) {
    case UserShortType.USER_SHORT_TYPE_CALLS_MADE:
      return updatePhoneCallsMadeAnimation(numberedValue1);
    case UserShortType.USER_SHORT_TYPE_FASTEST_RESPONSE_TIME_SECONDS:
      return updateFastestResponseTimeAnimation(numberedValue1);
    case UserShortType.USER_SHORT_TYPE_LONGEST_CALL_DURATION_SECONDS:
      return updateLongestCallDurationAnimation(Math.floor(numberedValue1 / 60));
    case UserShortType.USER_SHORT_TYPE_SMS_SENT:
      return updateTotalMessagesSentAnimation(numberedValue1);
    case UserShortType.USER_SHORT_TYPE_TOP_COMMUNICATION_CHANNEL: {
      if (numberedValue1 === 0) {
        return topCommunicationChannelMessagesAnimation;
      } else if (numberedValue1 === 1) {
        return topCommunicationChannelPhonesAnimation;
      } else if (numberedValue1 === 2) {
        return topCommunicationChannelPaymentsAnimation;
      }
      return null;
    }
    default:
      return null;
  }
};
